/**
 * The facts a PMS cannot tell Vale about a matter, collected from the user before import.
 *
 * Rendered only for providers whose config declares it needs them (ActionStep today). Each field is
 * here because the PMS genuinely has no equivalent, not as a preference:
 *
 * - **Vale matter type** — ActionStep's catalogue is per-firm and has no probate type, so there is
 *   nothing to map from.
 * - **State** — an ActionStep `Action` carries no jurisdiction at all.
 * - **Deceased person** — ActionStep has no native `Deceased` participant type, and an estate
 *   administration whose decedent Vale cannot identify is a matter its own surfaces cannot render.
 *   Selected from the matter's people when one of them is the decedent; typed instead when none of
 *   them is, or the user chooses to type rather than pick (`[ASTEP-23]`, `config.allowsTypedDecedentName`).
 *
 * The last two apply to estate administration only, gated on `isEstateAdminType` — the frontend's
 * single estate/non-estate predicate — so a conveyancing or family-law import asks for neither.
 */

'use client';

import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Banner } from '@/components/ui/banner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { AUSTRALIAN_STATES } from '@/lib/constants/australian-constants';
import { useMatterTypes } from '@/lib/services/matter-types';
import { selectableMatterTypes } from '@/lib/config/selectable-matter-types';
import { useOptionalFeatureFlags } from '@/lib/providers/feature-flags-provider';
import { usePmsMatterContacts } from '@/lib/services/integrations/pms';
import type { PmsProvider } from '@/lib/services/integrations/pms';
import { isEstateAdminType } from '@/lib/utils/matter-display';
import type { PmsImportProviderConfig } from './import-provider-config';

export interface ImportOptionsValue {
  valeMatterType: string;
  state: string;
  decedentExternalContactId: string;
  /** The decedent's given name, typed instead of chosen — see `PmsImportProviderConfig.allowsTypedDecedentName`. */
  decedentFirstName: string;
  /** The decedent's family name — see `decedentFirstName`. */
  decedentLastName: string;
}

export const EMPTY_IMPORT_OPTIONS: ImportOptionsValue = {
  valeMatterType: '',
  state: '',
  decedentExternalContactId: '',
  decedentFirstName: '',
  decedentLastName: ''
};

/**
 * Whether the options the provider requires have all been supplied.
 *
 * Exported so the modal's Confirm button and this component agree on completeness by construction
 * rather than by two copies of the same conditions.
 */
export function areImportOptionsComplete(
  config: PmsImportProviderConfig,
  value: ImportOptionsValue
): boolean {
  if (!config.requiresValeMatterType) return true;
  if (!value.valeMatterType) return false;

  if (!isEstateAdminType(value.valeMatterType)) return true;

  if (config.requiresState && !value.state) return false;

  if (config.requiresDecedentSelection) {
    const hasSelection = !!value.decedentExternalContactId;
    const hasTypedName =
      !!value.decedentFirstName.trim() && !!value.decedentLastName.trim();

    if (!hasSelection && !hasTypedName) return false;
  }

  return true;
}

export function ImportOptions({
  config,
  provider,
  externalMatterId,
  value,
  onChange
}: {
  config: PmsImportProviderConfig;
  provider: PmsProvider;
  externalMatterId: string;
  value: ImportOptionsValue;
  onChange: (value: ImportOptionsValue) => void;
}) {
  const { data: allMatterTypes, isLoading: isLoadingTypes } = useMatterTypes();
  // VALE-1562 — `matter-types` decides whether a firm works in the non-estate
  // practice areas, and `GET /matter-types` serves every active type regardless
  // (see the same gate in `session-banners.tsx`, VALE-1416). Without this, a
  // firm restricted everywhere else could import a PMS matter as any type from
  // here. The membership lives in `selectable-matter-types.ts` (VALE-1705)
  // rather than inline here.
  const { hasFeature } = useOptionalFeatureFlags();
  const matterTypes = selectableMatterTypes(
    allMatterTypes,
    hasFeature('matter-types')
  );

  const needsEstateFields = isEstateAdminType(value.valeMatterType);
  const needsDecedent = config.requiresDecedentSelection && needsEstateFields;

  const {
    data: matterContacts,
    isLoading: isLoadingContacts,
    isError: contactsFailed
  } = usePmsMatterContacts(provider, externalMatterId, needsDecedent);

  // A company cannot be a decedent, and Vale writes the decedent as a first/last name a company
  // record has no equivalent of — so organisations are not offered as choices.
  const selectableDecedents = (matterContacts ?? []).filter(
    (contact) => !contact.isCompany
  );

  const set = (patch: Partial<ImportOptionsValue>) =>
    onChange({ ...value, ...patch });

  const handleMatterTypeChange = (slug: string) => {
    // Leaving estate administration retires both estate-only answers rather than carrying them along
    // invisibly — a state and a decedent chosen for a probate import must not ride into, say, a
    // conveyancing one just because the user changed their mind about the type.
    set(
      isEstateAdminType(slug)
        ? { valeMatterType: slug }
        : {
            valeMatterType: slug,
            state: '',
            decedentExternalContactId: '',
            decedentFirstName: '',
            decedentLastName: ''
          }
    );
  };

  return (
    <div className="space-y-4 rounded-lg border p-4">
      <p className="text-sm font-medium">Import as</p>

      {config.requiresValeMatterType && (
        <div className="space-y-1.5">
          <Label htmlFor="import-matter-type" className="text-xs">
            Matter type
          </Label>
          <Select
            value={value.valeMatterType}
            onValueChange={handleMatterTypeChange}
            disabled={isLoadingTypes}
          >
            <SelectTrigger id="import-matter-type">
              <SelectValue
                placeholder={
                  isLoadingTypes
                    ? 'Loading matter types...'
                    : 'Select a matter type'
                }
              />
            </SelectTrigger>
            <SelectContent>
              {matterTypes.map((matterType) => (
                <SelectItem key={matterType.slug} value={matterType.slug}>
                  {matterType.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-muted-foreground text-xs">
            {config.label} has no matter type Vale can map from, so choose the
            type this matter should be created as.
          </p>
        </div>
      )}

      {config.requiresState && needsEstateFields && (
        <div className="space-y-1.5">
          <Label htmlFor="import-matter-state" className="text-xs">
            State
          </Label>
          <Select value={value.state} onValueChange={(state) => set({ state })}>
            <SelectTrigger id="import-matter-state">
              <SelectValue placeholder="Select a state" />
            </SelectTrigger>
            <SelectContent>
              {AUSTRALIAN_STATES.map((state) => (
                <SelectItem key={state.value} value={state.value}>
                  {state.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-muted-foreground text-xs">
            The jurisdiction this estate is administered in. {config.label} does
            not record one.
          </p>
        </div>
      )}

      {needsDecedent && (
        <DecedentField
          config={config}
          decedentExternalContactId={value.decedentExternalContactId}
          decedentFirstName={value.decedentFirstName}
          decedentLastName={value.decedentLastName}
          onChange={set}
          contacts={selectableDecedents}
          isLoading={isLoadingContacts}
          hasFailed={contactsFailed}
        />
      )}
    </div>
  );
}

function DecedentField({
  config,
  decedentExternalContactId,
  decedentFirstName,
  decedentLastName,
  onChange,
  contacts,
  isLoading,
  hasFailed
}: {
  config: PmsImportProviderConfig;
  decedentExternalContactId: string;
  decedentFirstName: string;
  decedentLastName: string;
  onChange: (
    patch: Partial<
      Pick<
        ImportOptionsValue,
        'decedentExternalContactId' | 'decedentFirstName' | 'decedentLastName'
      >
    >
  ) => void;
  contacts: Array<{
    externalContactId: string;
    displayName: string;
    externalRoleName: string | null;
    isConfiguredDecedent: boolean;
  }>;
  isLoading: boolean;
  hasFailed: boolean;
}) {
  // Only meaningful once contacts have loaded and there is a list to fall back to — switching to typed
  // entry clears the selection, and switching back clears the typed name, so the two never both carry a
  // value at once.
  const [typingManually, setTypingManually] = useState(false);

  const configuredDecedent = contacts.find(
    (contact) => contact.isConfiguredDecedent
  );

  // Adopt the firm's configured deceased contact once the list arrives — the backend sets that
  // flag only when the mapping resolves to exactly ONE person here, so it is never a guess between
  // candidates. Guarded on an empty selection so it can only ever fill a blank, never overwrite a
  // choice the user has already made.
  useEffect(() => {
    if (!decedentExternalContactId && configuredDecedent && !typingManually) {
      onChange({
        decedentExternalContactId: configuredDecedent.externalContactId
      });
    }
  }, [decedentExternalContactId, configuredDecedent, typingManually, onChange]);

  if (isLoading) {
    return (
      <div className="text-muted-foreground flex items-center gap-2 text-sm">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading the matter’s people...
      </div>
    );
  }

  const canType = config.allowsTypedDecedentName;

  // A failed read or an empty list leave nothing to pick from — go straight to typed entry rather than
  // refusing outright, when the provider supports it. A caution banner still explains why there's no
  // list, but it no longer blocks the import.
  if (hasFailed) {
    return canType ? (
      <div className="space-y-3">
        <Banner
          tone="caution"
          title={`Could not read this matter’s people from ${config.label}.`}
          line="Type the deceased person’s name instead."
        />
        <TypedDecedentNameFields
          firstName={decedentFirstName}
          lastName={decedentLastName}
          onChange={onChange}
        />
      </div>
    ) : (
      <Banner
        tone="caution"
        title={`Could not read this matter’s people from ${config.label}.`}
        line="Close and try again. A deceased person must be chosen before an estate administration can be imported."
      />
    );
  }

  if (contacts.length === 0) {
    return canType ? (
      <TypedDecedentNameFields
        firstName={decedentFirstName}
        lastName={decedentLastName}
        onChange={onChange}
        helperText={`This matter has no people on it in ${config.label}. Type the deceased person’s name instead.`}
      />
    ) : (
      <Banner
        tone="caution"
        title="This matter has no people on it."
        line={`An estate administration needs a deceased person. Add them to the matter in ${config.label}, then import it.`}
      />
    );
  }

  if (canType && typingManually) {
    return (
      <div className="space-y-1.5">
        <TypedDecedentNameFields
          firstName={decedentFirstName}
          lastName={decedentLastName}
          onChange={onChange}
        />
        <Button
          pad="flush"
          density="micro"
          size="auto"
          type="button"
          variant="link"
          onClick={() => {
            setTypingManually(false);
            onChange({ decedentFirstName: '', decedentLastName: '' });
          }}
        >
          Choose from the matter’s people instead
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-1.5">
      <Label htmlFor="import-matter-decedent" className="text-xs">
        Deceased person
      </Label>
      <Select
        value={decedentExternalContactId}
        onValueChange={(id) => onChange({ decedentExternalContactId: id })}
      >
        <SelectTrigger id="import-matter-decedent">
          <SelectValue placeholder="Select the deceased person" />
        </SelectTrigger>
        <SelectContent>
          {contacts.map((contact) => (
            <SelectItem
              key={contact.externalContactId}
              value={contact.externalContactId}
              description={contact.externalRoleName ?? undefined}
            >
              {contact.displayName}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <p className="text-muted-foreground text-xs">
        Whose estate this matter administers. {config.label} has no deceased
        role, so only the person chosen here is imported as deceased.
      </p>
      {canType && (
        <Button
          pad="flush"
          density="micro"
          size="auto"
          type="button"
          variant="link"
          onClick={() => {
            setTypingManually(true);
            onChange({ decedentExternalContactId: '' });
          }}
        >
          Not on this list? Enter their name manually
        </Button>
      )}
    </div>
  );
}

function TypedDecedentNameFields({
  firstName,
  lastName,
  onChange,
  helperText
}: {
  firstName: string;
  lastName: string;
  onChange: (
    patch: Partial<
      Pick<ImportOptionsValue, 'decedentFirstName' | 'decedentLastName'>
    >
  ) => void;
  helperText?: string;
}) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor="import-matter-decedent-first-name" className="text-xs">
        Deceased person’s name
      </Label>
      <div className="flex gap-2">
        <Input
          id="import-matter-decedent-first-name"
          placeholder="First name"
          value={firstName}
          onChange={(e) => onChange({ decedentFirstName: e.target.value })}
        />
        <Input
          id="import-matter-decedent-last-name"
          placeholder="Last name"
          value={lastName}
          onChange={(e) => onChange({ decedentLastName: e.target.value })}
        />
      </div>
      <p className="text-muted-foreground text-xs">
        {helperText ??
          'Whose estate this matter administers. Type their name since no matter participant identifies them.'}
      </p>
    </div>
  );
}
